/* ==========================================================================
   Lovelle — theme.js
   Core interactivity, no animation libraries. Runs on EVERY device.
   Handles: scroll-reveal (IntersectionObserver), header, mobile nav,
   accordions, cart drawer, add-to-cart (AJAX), add-to-cart upsell popup
   (Shopify product recommendations), sticky ATC, gallery, variants, email popup.
   ========================================================================== */
(function () {
  'use strict';
  var H = window.Lovelle || { routes: {}, strings: {} };
  var ROOT = (H.routes && H.routes.root) ? H.routes.root : '/';
  if (ROOT.charAt(ROOT.length - 1) !== '/') ROOT += '/'; // '/en' -> '/en/'
  var reduced = window.matchMedia('(prefers-reduced-motion:reduce)').matches;
  document.documentElement.classList.add('js');

  /* Scroll lock derived from ACTUAL open overlays (no fragile counter).
     Body scroll is frozen only while at least one overlay is open, and is
     always released once they're all closed — immune to open/close ordering. */
  function updateScrollLock() {
    var anyOpen = document.querySelector('.cart-drawer.is-open, .mobile-menu.is-open, .upsell-popup.is-open, .filter-drawer.is-open');
    document.body.style.overflow = anyOpen ? 'hidden' : '';
  }

  var fmt = null;
  var fmtKey = null;

  /* Fallback only. The real answer comes from H.moneyDecimals, which Liquid
     probes off this market's actual money format (see layout/theme.liquid).
     A hardcoded list was wrong here once already: SEK was assumed to be whole
     kronor, but the shop renders "399,00 kr", so the drawer and the page
     disagreed. Kept for the case where the config object is absent. */
  var ZERO_DECIMAL_DISPLAY = { SEK: 1, NOK: 1, DKK: 1, ISK: 1 };

  function money(cents, forceDigits) {
    var amount = cents / 100;
    var cur = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'SEK';
    // Formatting follows the page's own language rather than a fixed sv-SE,
    // so a Norwegian storefront groups and spaces numbers the Norwegian way.
    var lang = document.documentElement.lang || 'sv-SE';
    // Liquid probed the market's real money format and put the answer in the
    // config; trust it over the table above. The table only covers the case
    // where the config is missing (an old layout, a section rendered alone).
    var digits = (typeof forceDigits === 'number')
      ? forceDigits
      : (H && typeof H.moneyDecimals === 'number')
        ? H.moneyDecimals
        : (ZERO_DECIMAL_DISPLAY[cur] ? 0 : 2);
    // The formatter is memoised, so it has to remember what it was built for.
    // Without the key a currency switch after first paint keeps the old one.
    var key = lang + '|' + cur + '|' + digits;
    try {
      if (!fmt || fmtKey !== key) {
        fmt = new Intl.NumberFormat(lang, {
          style: 'currency',
          currency: cur,
          minimumFractionDigits: digits,
          maximumFractionDigits: digits
        });
        fmtKey = key;
      }
      return fmt.format(amount);
    } catch (e) {
      // Never hardcode a symbol here: a euro cart falling back to "80 kr" is
      // worse than an unstyled number.
      return amount.toFixed(digits) + ' ' + cur;
    }
  }

  /* The free-shipping meter talks in round numbers: "349 kr", "50 kr kvar",
     never "349,00 kr". A whole amount drops its decimals; anything with öre
     or cents keeps them ("50,10 kr", "34,90 €"), which is the same rule as
     Liquid's money_without_trailing_zeros that seeds the drawer. */
  function moneyClean(cents) {
    return (cents % 100 === 0) ? money(cents, 0) : money(cents);
  }

  /* ============================================================
     SHARED: modern custom dropdown + colour swatches
     Used by the leggings bundle AND the standard product page.
     ============================================================ */
  /* On a localised storefront the option value arrives already translated, so
     "Svart" is "Sort" and "Grön" is "Grønn". Every colour therefore carries its
     Swedish, Norwegian, Danish and English spellings — a name that matches
     nothing falls through to the neutral grey, which is what turned a whole
     Norwegian colour picker into identical grey dots.

     Longest-first matters: "mörkgrå" and "mørkegrå" have to be tested before
     "grå"/"gra", or every dark grey comes out mid-grey. */
  var LOVELLE_SW = [
    ['mörkgrå','#3d3d3d'],['grigio scuro','#3d3d3d'],['morkgra','#3d3d3d'],['mørkegrå','#3d3d3d'],['morkegra','#3d3d3d'],
    ['tummanharmaa','#3d3d3d'],['grafit','#3d3d3d'],['dark grey','#3d3d3d'],['dark gray','#3d3d3d'],
    ['tummanruskea','#4a3527'],['mörkbrun','#4a3527'],['marrone scuro','#4a3527'],['mørkebrun','#4a3527'],['morkbrun','#4a3527'],['morkebrun','#4a3527'],
    ['tummanvihreä','#2f5138'],['mørkegrøn','#2f5138'],['oliivinvihreä','#6b6b3a'],['olivengrøn','#6b6b3a'],['mörkgrön','#2f5138'],['verde scuro','#2f5138'],['mørkegrønn','#2f5138'],['olivgrön','#6b6b3a'],['verde oliva','#6b6b3a'],['olivengrønn','#6b6b3a'],
    ['vaaleanharmaa','#c8c8c8'],['ljusgrå','#c8c8c8'],['grigio chiaro','#c8c8c8'],['ljusgra','#c8c8c8'],['lysegrå','#c8c8c8'],['lysegra','#c8c8c8'],['light grey','#c8c8c8'],
    ['vaaleansininen','#a8c8e8'],['ljusblå','#a8c8e8'],['azzurro baby','#bcd8ef'],['azzurro','#a8c8e8'],['ljusbla','#a8c8e8'],['lyseblå','#a8c8e8'],['lysebla','#a8c8e8'],
    ['vauvansininen','#bcd8ef'],['bebisblå','#bcd8ef'],['babyblå','#bcd8ef'],['babybla','#bcd8ef'],['baby blue','#bcd8ef'],
    ['taivaansininen','#7fb2e5'],['himmelblå','#7fb2e5'],['celeste','#7fb2e5'],['himmelbla','#7fb2e5'],['sky blue','#7fb2e5'],
    ['kuninkaansininen','#2a4bd7'],['kungsblå','#2a4bd7'],['kungsbla','#2a4bd7'],['kongeblå','#2a4bd7'],['royal','#2a4bd7'],
    ['laivastonsininen','#1b2a4a'],['tummansininen','#1b2a4a'],['mørkeblå','#1b2a4a'],['marinblå','#1b2a4a'],['blu navy','#1b2a4a'],['blu scuro','#1b2a4a'],['marineblå','#1b2a4a'],['marin','#1b2a4a'],['marine','#1b2a4a'],['navy','#1b2a4a'],
    ['viininpunainen','#6e1b2e'],['vinröd','#6e1b2e'],['vinrod','#6e1b2e'],['vinrød','#6e1b2e'],['bordeaux','#6e1b2e'],['wine','#6e1b2e'],
    ['rubiininpunainen','#9b1b30'],['rubinröd','#9b1b30'],['rubino','#9b1b30'],['rubinrød','#9b1b30'],['ruby','#9b1b30'],
    ['neonpinkki','#ff4fa3'],['pinkki','#ff4fa3'],['knallrosa','#ff4fa3'],['bright pink','#ff4fa3'],['lavendel','#b9a6d4'],['lavanda','#b9a6d4'],['laventeli','#b9a6d4'],['lavender','#b9a6d4'],
    ['vaaleanliila','#c4b0dd'],['ljuslila','#c4b0dd'],['lilla chiaro','#c4b0dd'],['lyselilla','#c4b0dd'],['light purple','#c4b0dd'],
    ['riikinkukko','#1a8c7a'],['påfugleblå','#1a8c7a'],['påfågel','#1a8c7a'],['pafagel','#1a8c7a'],['påfugl','#1a8c7a'],['peacock','#1a8c7a'],['petrol','#1a8c7a'],
    ['minttuvihreä','#a8dcc4'],['mintgrøn','#a8dcc4'],['mintgrön','#a8dcc4'],['mint grön','#a8dcc4'],['verde menta','#a8dcc4'],['mintgrønn','#a8dcc4'],['minttu','#a8dcc4'],['mint','#a8dcc4'],
    ['kermanvalkoinen','#f3ece0'],['cremefarvet','#f3ece0'],['flødefarvet','#f3ece0'],['luonnonvalkoinen','#f0ece6'],['krämvit','#f3ece0'],['bianco panna','#f3ece0'],['panna','#f3ece0'],['kremhvit','#f3ece0'],['cream','#f3ece0'],['off-white','#f0ece6'],['off white','#f0ece6'],
    ['kahvinruskea','#6f4e37'],['kahvi','#6f4e37'],['kaffe','#6f4e37'],['marrone caffè','#6f4e37'],['caffè','#6f4e37'],['caffe','#6f4e37'],['coffee','#6f4e37'],['ruskea','#6f4e37'],['brun','#6f4e37'],['marrone','#6f4e37'],['brown','#6f4e37'],['samppanja','#e6d5b8'],['champagne','#e6d5b8'],
    ['mocka','#8a6a55'],['moka','#8a6a55'],['mokka','#8a6a55'],['mocha','#8a6a55'],['taupe','#a78e7b'],
    ['aprikoosi','#e8c9a0'],['aprikos','#e8c9a0'],['apricot','#e8c9a0'],['beige','#d9c2a6'],['nude','#d9c2a6'],['beessi','#d9c2a6'],['hiekka','#d9c2a6'],['sandfarvet','#d9c2a6'],['sand','#d9c2a6'],['sabbia','#d9c2a6'],['cammello','#c19a6b'],
    ['oliivi','#6b6b3a'],['oliv','#6b6b3a'],['oliva','#6b6b3a'],['olive','#6b6b3a'],['khaki','#8f8b5a'],['kaki','#8f8b5a'],
    ['turkoosi','#1aa7a0'],['turkos','#1aa7a0'],['turchese','#1aa7a0'],['turkis','#1aa7a0'],['teal','#1aa7a0'],
    ['oranssinpunainen','#e2552b'],['oranssi','#e2552b'],['orangeröd','#e2552b'],['orangerød','#e2552b'],['orange','#e2552b'],['koralli','#f0734e'],['koral','#f0734e'],['korall','#f0734e'],['coral','#f0734e'],
    ['meleerattu','#9a9a9a'],['meleret','#9a9a9a'],['melerad','#9a9a9a'],['melert','#9a9a9a'],['heather','#9a9a9a'],
    ['luumu','#7a3fa0'],['blomme','#7a3fa0'],['violetti','#7a3fa0'],['plommon','#7a3fa0'],['plomme','#7a3fa0'],['liila','#7a3fa0'],['lila','#7a3fa0'],['lilla','#7a3fa0'],['purple','#7a3fa0'],
    ['ruusu','#e7bfc2'],['pudderrosa','#e7bfc2'],['rosé','#e7bfc2'],['rose','#e7bfc2'],
    ['musta','#1b1b1b'],['svart','#1b1b1b'],['nero','#1b1b1b'],['sort','#1b1b1b'],['black','#1b1b1b'],
    ['valkoinen','#f1f1f1'],['hvid','#f1f1f1'],['vit','#f1f1f1'],['bianco','#f1f1f1'],['hvit','#f1f1f1'],['white','#f1f1f1'],
    ['vaaleanpunainen','#f2a1c0'],['lyserød','#f2a1c0'],['rosa','#f2a1c0'],['pink','#f2a1c0'],
    ['harmaa','#9a9a9a'],['grå','#9a9a9a'],['grigio','#9a9a9a'],['gra','#9a9a9a'],['grey','#9a9a9a'],['gray','#9a9a9a'],
    ['punainen','#c0392b'],['röd','#c0392b'],['rosso','#c0392b'],['rod','#c0392b'],['rød','#c0392b'],['red','#c0392b'],
    ['vihreä','#3fa34d'],['grøn','#3fa34d'],['grön','#3fa34d'],['verde','#3fa34d'],['gron','#3fa34d'],['grønn','#3fa34d'],['gronn','#3fa34d'],['green','#3fa34d'],
    ['sininen','#2a6fdb'],['blå','#2a6fdb'],['blu','#2a6fdb'],['bla','#2a6fdb'],['blue','#2a6fdb'],['keltainen','#e8c33a'],['gul','#e8c33a'],['giallo','#e8c33a'],['yellow','#e8c33a']
  ];
  function swatchFor(name) {
    var n = (name || '').toLowerCase();
    if (n.indexOf('batik') > -1 || n.indexOf('batiik') > -1 || n.indexOf('tie') > -1 || n.indexOf('dye') > -1) {
      if (n.indexOf('blå') > -1 || n.indexOf('bla') > -1 || n.indexOf('sininen') > -1 || n.indexOf('blue') > -1) return 'conic-gradient(#2a6fdb,#7a3fa0,#1aa7a0,#2a6fdb)';
      return 'conic-gradient(#7a3fa0,#ff4fa3,#2a6fdb,#7a3fa0)';
    }
    /* Prints are not a flat colour. The ground colour is whatever colour word
       sits next to "leopard"/"prickig"/"prikkete", and the spots are drawn on
       top, so "Leopard beige" and "Prikkete sort" read as prints rather than as
       two identical dots. */
    var isLeopard = n.indexOf('leopard') > -1 || n.indexOf('gepard') > -1;
    var isDotted = n.indexOf('prick') > -1 || n.indexOf('prikk') > -1 || n.indexOf('pilkku') > -1 || n.indexOf('täplik') > -1 || n.indexOf('dot') > -1 || n.indexOf('flekk') > -1 || n.indexOf('pois') > -1;
    if (isLeopard || isDotted) {
      var ground = flatSwatch(n.replace('leopard', '').replace('gepard', '').replace('prickig', '').replace('prikkete', '').replace('prikket', '').replace('pilkullinen', '').replace('täplikäs', '').replace('a pois', '').replace('pois', ''));
      var spot = 'rgba(40,28,20,.72)';
      if (n.indexOf('svart') > -1 || n.indexOf('sort') > -1 || n.indexOf('musta') > -1 || n.indexOf('black') > -1 || n.indexOf('nero') > -1) {
        ground = '#2a2320'; spot = 'rgba(255,255,255,.55)';
      }
      var size = isDotted ? '7px 7px' : '9px 9px';
      return 'radial-gradient(' + spot + ' 34%, transparent 36%) 0 0/' + size +
             ', radial-gradient(' + spot + ' 30%, transparent 32%) 4px 4px/' + size +
             ', ' + ground;
    }
    return flatSwatch(n);
  }
  function flatSwatch(n) {
    for (var i = 0; i < LOVELLE_SW.length; i++) { if (n.indexOf(LOVELLE_SW[i][0]) > -1) return LOVELLE_SW[i][1]; }
    return '#cfcabf';
  }
  // colorImg: { colourValue: imageUrl }. Builds the dropdown over a native <select>.
  function buildCustomSelect(sel, colorImg) {
    colorImg = colorImg || {};
    var isColor = sel.hasAttribute('data-color');
    var wrap = document.createElement('div');
    wrap.className = 'cselect' + (isColor ? ' cselect--color' : '') + (sel.classList.contains('bundle__select--size') ? ' cselect--size' : '');
    var btn = document.createElement('button');
    btn.type = 'button'; btn.className = 'cselect__btn'; btn.setAttribute('aria-haspopup', 'listbox'); btn.setAttribute('aria-expanded', 'false');
    var menu = document.createElement('div'); menu.className = 'cselect__menu'; menu.setAttribute('role', 'listbox'); menu.hidden = true;
    function sw(name) {
      if (!isColor) return '';
      if (colorImg[name]) return '<i class="cselect__sw cselect__sw--img" style="background-image:url(' + colorImg[name] + ')"></i>';
      return '<i class="cselect__sw" style="background:' + swatchFor(name) + '"></i>';
    }
    function close() { menu.hidden = true; btn.setAttribute('aria-expanded', 'false'); wrap.classList.remove('is-open'); }
    function setVal(val) {
      sel.value = val;
      btn.innerHTML = '<span class="cselect__cur">' + sw(val) + '<span>' + val + '</span></span><span class="cselect__caret"></span>';
      Array.prototype.forEach.call(menu.querySelectorAll('.cselect__opt'), function (o) { o.setAttribute('aria-selected', o.getAttribute('data-value') === val ? 'true' : 'false'); });
      sel.dispatchEvent(new Event('change', { bubbles: true }));
    }
    Array.prototype.forEach.call(sel.options, function (opt) {
      var o = document.createElement('div'); o.className = 'cselect__opt'; o.setAttribute('role', 'option'); o.setAttribute('data-value', opt.value);
      o.innerHTML = sw(opt.value) + '<span>' + opt.textContent + '</span>';
      o.addEventListener('click', function () { setVal(opt.value); close(); });
      menu.appendChild(o);
    });
    btn.addEventListener('click', function (e) { e.stopPropagation(); if (menu.hidden) { menu.hidden = false; btn.setAttribute('aria-expanded', 'true'); wrap.classList.add('is-open'); } else close(); });
    document.addEventListener('click', function (e) { if (!wrap.contains(e.target)) close(); });
    wrap.appendChild(btn); wrap.appendChild(menu);
    sel.style.display = 'none';
    sel.parentNode.insertBefore(wrap, sel.nextSibling);
    setVal(sel.value || (sel.options[0] && sel.options[0].value) || '');
  }
  // Build a { colourValue: thumbUrl } / { colourValue: imagePosition } map from a product's variants.
  function colourImageMaps(product, colorPos) {
    var img = {}, pos = {};
    if (colorPos) {
      product.variants.forEach(function (vr) {
        var cv = vr.options[colorPos - 1];
        if (cv && !img[cv] && vr.featured_image && vr.featured_image.src) {
          var s = vr.featured_image.src;
          img[cv] = s + (s.indexOf('?') > -1 ? '&' : '?') + 'width=80&height=80&crop=center';
          pos[cv] = vr.featured_image.position;
        }
      });
    }
    return { img: img, pos: pos };
  }

  /* ============================================================
     1. SCROLL REVEAL — dependency-free, works on mobile + desktop
     ============================================================ */
  (function () {
    var items = document.querySelectorAll('.reveal-item');
    if (!items.length) return;
    if (reduced || !('IntersectionObserver' in window)) {
      items.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          var el = entry.target;
          // gentle stagger based on position among siblings
          var siblings = el.parentElement ? el.parentElement.querySelectorAll('.reveal-item') : [el];
          var idx = Array.prototype.indexOf.call(siblings, el);
          el.style.transitionDelay = Math.min(idx, 6) * 70 + 'ms';
          el.classList.add('is-visible');
          io.unobserve(el);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    items.forEach(function (el) { io.observe(el); });
    // Safety net: reveal anything still hidden after 2.5s (e.g. zero-height edge cases)
    setTimeout(function () {
      document.querySelectorAll('.reveal-item:not(.is-visible)').forEach(function (el) {
        var r = el.getBoundingClientRect();
        if (r.top < window.innerHeight + 200) el.classList.add('is-visible');
      });
    }, 2500);
  })();

  /* ============================================================
     2. HEADER + MOBILE NAV
     ============================================================ */
  var header = document.querySelector('.site-header');
  if (header) {
    var onScroll = function () { header.classList.toggle('scrolled', window.scrollY > 60); };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }
  var mobileMenu = document.querySelector('.mobile-menu');
  var menuOverlay = document.querySelector('.mobile-menu__overlay');
  var menuToggles = document.querySelectorAll('[data-menu-open]');
  function setToggleState(on) {
    menuToggles.forEach(function (b) { b.setAttribute('aria-expanded', on ? 'true' : 'false'); });
  }
  function openMenu() {
    if (!mobileMenu) return;
    mobileMenu.classList.add('is-open');
    if (menuOverlay) menuOverlay.classList.add('is-active');
    mobileMenu.setAttribute('aria-hidden', 'false');
    setToggleState(true);
    updateScrollLock();
  }
  function closeNav() {
    if (!mobileMenu) return;
    if (!mobileMenu.classList.contains('is-open')) return;
    mobileMenu.classList.remove('is-open');
    if (menuOverlay) menuOverlay.classList.remove('is-active');
    mobileMenu.setAttribute('aria-hidden', 'true');
    setToggleState(false);
    updateScrollLock();
  }
  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-menu-open]')) { e.preventDefault(); openMenu(); return; }
    if (e.target.closest('[data-menu-close]')) { closeNav(); return; }
  });
  // Close the menu when a navigation link inside it is followed
  if (mobileMenu) {
    mobileMenu.querySelectorAll('.mobile-menu__nav a').forEach(function (a) {
      a.addEventListener('click', function () { closeNav(); });
    });
  }

  /* ============================================================
     3. ACCORDIONS
     ============================================================ */
  document.querySelectorAll('.accordion__trigger').forEach(function (btn) {
    btn.addEventListener('click', function () {
      var expanded = btn.getAttribute('aria-expanded') === 'true';
      btn.setAttribute('aria-expanded', String(!expanded));
      var body = btn.nextElementSibling;
      if (body) body.hidden = expanded;
    });
  });

  /* ============================================================
     4. CART DRAWER
     ============================================================ */
  var drawer = document.querySelector('.cart-drawer');
  var overlay = document.querySelector('.cart-overlay');

  function openCart() {
    if (!drawer) return;
    closeNav();
    drawer.classList.add('is-open');
    if (overlay) overlay.classList.add('is-active');
    drawer.setAttribute('aria-hidden', 'false');
    updateScrollLock();
  }
  function closeCart() {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    if (overlay) overlay.classList.remove('is-active');
    drawer.setAttribute('aria-hidden', 'true');
    updateScrollLock();
  }
  window.LovelleOpenCart = openCart;
  window.LovelleAddToCart = addToCart;

  // Event delegation so dynamically-added triggers work too
  document.addEventListener('click', function (e) {
    var toggle = e.target.closest('[data-cart-toggle]');
    if (toggle) { e.preventDefault(); openCart(); fetchCart(); return; }
    if (e.target.closest('[data-cart-close]')) { closeCart(); return; }
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') { closeCart(); closeUpsell(); closeNav(); }
  });

  function renderCart(cart) {
    document.querySelectorAll('.cart-count').forEach(function (b) {
      b.textContent = cart.item_count;
      b.style.display = cart.item_count > 0 ? 'flex' : 'none';
    });
    if (!drawer) return;
    var itemsEl = drawer.querySelector('.cart-drawer__items');
    var footEl = drawer.querySelector('.cart-drawer__foot');
    var countInline = drawer.querySelector('[data-cart-count-inline]');
    if (!itemsEl) return;
    if (countInline) countInline.textContent = cart.item_count > 0 ? '(' + cart.item_count + ')' : '';

    var empty = !cart.items || cart.items.length === 0;
    if (footEl) { footEl.hidden = empty; footEl.style.display = ''; }

    var html = '';
    if (empty) {
      html = '<div class="cart-empty">' + (H.strings.cartEmpty || '') + '</div>';
      cart.items = [];
    }
    cart.items.forEach(function (item) {
      var img = item.image ? item.image.replace(/(_(\d+)x(\d+))?(\.[^.?]*)(\?.*)?$/, '_120x$4') : '';
      html += '<div class="cart-line" data-key="' + item.key + '">' +
        (img ? '<img src="' + img + '" alt="" width="64" height="80" loading="lazy">' : '<span></span>') +
        '<div><div class="cart-line__title">' + item.product_title + '</div>' +
        (item.variant_title && item.variant_title !== 'Default Title' ? '<div class="cart-line__variant">' + item.variant_title + '</div>' : '') +
        '<div class="cart-line__qty"><button data-qty-down aria-label="' + (H.strings.decrease || '') + '">−</button>' +
        '<span>' + item.quantity + '</span><button data-qty-up aria-label="' + (H.strings.increase || '') + '">+</button></div></div>' +
        '<div><div class="cart-line__price">' + money(item.final_line_price) + '</div>' +
        '<button class="cart-line__remove" data-remove>' + (H.strings.remove || '') + '</button></div></div>';
    });
    itemsEl.innerHTML = html;

    // The threshold and all three sentences arrive from Liquid (window.Lovelle.
    // shipping), so the bar counts in the market's own currency and reads in the
    // market's own language. The old version had eleven Swedish sentences and a
    // hardcoded 399 baked in here, which no translation could reach.
    var ship = (H.shipping || {});
    var threshold = ship.threshold || 34900;
    var shipBox = drawer.querySelector('[data-cart-ship]');
    var bar = drawer.querySelector('[data-cart-ship-bar]') || drawer.querySelector('.cart-ship-bar i');
    var note = drawer.querySelector('[data-cart-ship-note]') || drawer.querySelector('.cart-ship-note');
    var reached = !empty && cart.total_price >= threshold;
    if (bar) {
      var pct = Math.min(100, (cart.total_price / threshold) * 100);
      bar.style.width = pct + '%';
      var track = bar.parentNode;
      if (track && track.setAttribute) track.setAttribute('aria-valuenow', Math.round(pct));
    }
    if (shipBox) shipBox.classList.toggle('is-reached', reached);
    if (note) {
      var msg;
      if (reached) {
        msg = ship.reached || '';
      } else if (cart.total_price === 0) {
        msg = (ship.over || '').replace('%s', moneyClean(threshold));
      } else {
        msg = (ship.remaining || '').replace('%s', moneyClean(threshold - cart.total_price));
      }
      note.textContent = msg;
    }
    var totalEl = drawer.querySelector('[data-cart-total]') || drawer.querySelector('.cart-total span:last-child');
    if (totalEl) totalEl.textContent = money(cart.total_price);

    updateUpsells(cart);
  }

  /* ---- Upsell row ------------------------------------------------------
     A horizontal row of one-tap products under the line items. The cards are
     NOT built here: theme.js asks Shopify to render sections/cart-upsells.liquid
     through the Section Rendering API, so every price is this market's price
     and the copy is this language's copy. Two sources, decided by the theme
     setting: a curated collection, or Shopify's related products for the first
     line in the cart. Whatever is already in the cart is hidden; if nothing is
     left, so is the row. Fetched once per source and cached — opening and
     closing the drawer costs nothing after the first time. */
  var upsellBox = drawer ? drawer.querySelector('[data-cart-upsell]') : null;
  var upsellTrack = drawer ? drawer.querySelector('[data-cart-upsell-track]') : null;
  var upsellCache = {};
  var upsellPending = null;
  var upsellShownFor = null;

  function upsellUrl(cart) {
    var cfg = H.cart || {};
    if (cfg.upsellEnabled === false || !upsellTrack) return null;
    if (cfg.upsellCollection) return ROOT + '?section_id=cart-upsells';
    var first = cart && cart.items && cart.items[0];
    if (first && first.product_id) {
      return ROOT + 'recommendations/products?section_id=cart-upsells&limit=8&intent=related&product_id=' + first.product_id;
    }
    return ROOT + '?section_id=cart-upsells';
  }

  function filterUpsells(cart) {
    if (!upsellBox || !upsellTrack) return;
    var inCart = {};
    ((cart && cart.items) || []).forEach(function (i) { inCart[String(i.product_id)] = 1; });
    var visible = 0;
    Array.prototype.forEach.call(upsellTrack.querySelectorAll('[data-product-id]'), function (card) {
      var hide = !!inCart[card.getAttribute('data-product-id')];
      card.classList.toggle('is-in-cart', hide);
      if (!hide) visible++;
    });
    upsellBox.hidden = visible === 0;
  }

  function updateUpsells(cart) {
    var url = upsellUrl(cart);
    if (!url) { if (upsellBox) upsellBox.hidden = true; return; }
    if (upsellCache[url] !== undefined) {
      if (upsellShownFor !== url) {
        upsellTrack.innerHTML = upsellCache[url];
        upsellShownFor = url;
        upsellTrack.scrollLeft = 0;
      }
      filterUpsells(cart);
      return;
    }
    if (upsellPending === url) return;
    upsellPending = url;
    fetch(url, { headers: { 'Accept': 'text/html' } })
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (txt) {
        upsellPending = null;
        var tmp = document.createElement('div');
        tmp.innerHTML = txt;
        var list = tmp.querySelector('[data-cart-upsell-list]');
        upsellCache[url] = list ? list.innerHTML : '';
        upsellTrack.innerHTML = upsellCache[url];
        upsellShownFor = url;
        upsellTrack.scrollLeft = 0;
        filterUpsells(cart);
      })
      .catch(function () {
        upsellPending = null;
        if (upsellBox) upsellBox.hidden = true;
      });
  }

  // Delegated qty / remove inside drawer
  if (drawer) {
    drawer.addEventListener('click', function (e) {
      var line = e.target.closest('.cart-line');
      if (!line) return;
      var key = line.getAttribute('data-key');
      var qty = parseInt(line.querySelector('.cart-line__qty span').textContent, 10);
      if (e.target.closest('[data-qty-up]')) changeQty(key, qty + 1);
      else if (e.target.closest('[data-qty-down]')) changeQty(key, qty - 1);
      else if (e.target.closest('[data-remove]')) changeQty(key, 0);
    });
  }

  function fetchCart() {
    return fetch(ROOT + 'cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(renderCart)
      .catch(function () {});
  }
  function changeQty(key, quantity) {
    fetch((H.routes.cart_change_url || ROOT + 'cart/change.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    }).then(function (r) { return r.json(); }).then(renderCart).catch(function () {});
  }

  /* ============================================================
     5. ADD TO CART (AJAX) — delegated, covers dynamic forms
     ============================================================ */
  function addToCart(items, btn, opts) {
    opts = opts || {};
    var original = btn ? btn.innerHTML : null;
    if (btn) { btn.disabled = true; btn.innerHTML = '<span>…</span>'; }
    return fetch((H.routes.cart_add_url || ROOT + 'cart/add.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (btn) btn.innerHTML = '<span>' + (H.strings.addedToCart || 'Tillagd') + '</span>';
        if (window.fbq) try { window.fbq('track', 'AddToCart'); } catch (e) {}
        return fetchCart().then(function () {
          var pid = null;
          if (data && data.items && data.items[0]) pid = data.items[0].product_id;
          else if (data && data.product_id) pid = data.product_id;
          if (opts.showUpsell !== false && pid) { showUpsell(pid); }
          else if (opts.openCartAfter !== false) { openCart(); }
          // adding from inside the upsell popup => openCartAfter:false => keep popup,
          // just refresh the count (avoids stacking the cart over the popup)
        });
      })
      .catch(function () {})
      .then(function () {
        setTimeout(function () { if (btn) { btn.disabled = false; btn.innerHTML = original; } }, 1400);
      });
  }

  document.addEventListener('submit', function (e) {
    var form = e.target.closest('form[data-ajax-cart]');
    if (!form) return;
    e.preventDefault();
    var id = form.querySelector('[name="id"]');
    if (!id || !id.value) return;
    var qtyInput = form.querySelector('[name="quantity"]');
    var qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
    var fromUpsell = !!form.closest('[data-upsell-popup]');
    // Adding from the row inside the drawer: no popup on top of the drawer,
    // just re-render the lines — the card she tapped disappears from the row.
    var fromDrawer = !!form.closest('.cart-drawer');
    addToCart([{ id: parseInt(id.value, 10), quantity: qty }], form.querySelector('[type="submit"]'),
      { showUpsell: !fromUpsell && !fromDrawer, openCartAfter: !fromUpsell });
  });

  // Frequently-bought-together button
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-fbt-add]');
    if (!btn) return;
    var form = btn.closest('[data-fbt-form]');
    if (!form) return;
    var items = [];
    form.querySelectorAll('input[type="checkbox"]:checked').forEach(function (cb) {
      if (cb.value) items.push({ id: parseInt(cb.value, 10), quantity: 1 });
    });
    if (items.length) addToCart(items, btn, { showUpsell: true });
  });

  /* ============================================================
     6. ADD-TO-CART UPSELL POPUP — Shopify recommendations
     ============================================================ */
  var upsell = document.querySelector('[data-upsell-popup]');
  function closeUpsell() {
    if (!upsell) return;
    upsell.classList.remove('is-open');
    updateScrollLock();
  }
  if (upsell) {
    upsell.addEventListener('click', function (e) {
      if (e.target.closest('[data-upsell-close]')) closeUpsell();
      if (e.target.closest('[data-upsell-cart]')) { closeUpsell(); openCart(); }
    });
  }
  function showUpsell(productId) {
    if (!upsell) { openCart(); return; }
    var listEl = upsell.querySelector('[data-upsell-items]');
    var url = ROOT + 'recommendations/products?section_id=product-recommendations&limit=8&intent=related&product_id=' + productId;
    fetch(url)
      .then(function (r) { return r.text(); })
      .then(function (txt) {
        var tmp = document.createElement('div');
        tmp.innerHTML = txt;
        var found = tmp.querySelector('[data-rec-list]');
        if (found && found.children.length > 0) {
          listEl.innerHTML = found.innerHTML;
          upsell.classList.add('is-open');
          updateScrollLock();
        } else {
          openCart(); // no recommendations → just show the cart
        }
      })
      .catch(function () { openCart(); });
  }

  /* ============================================================
     7. STICKY MOBILE ATC
     ============================================================ */
  var mainAtc = document.querySelector('[data-product-form] [type="submit"]') || document.querySelector('[data-bundle-add]');
  var stickyAtc = document.querySelector('.sticky-atc');
  if (mainAtc && stickyAtc && 'IntersectionObserver' in window) {
    new IntersectionObserver(function (entries) {
      stickyAtc.classList.toggle('is-visible', !entries[0].isIntersecting);
    }, { rootMargin: '0px 0px -40px 0px' }).observe(mainAtc);
  }

  /* ============================================================
     8. PDP GALLERY THUMBNAILS
     ============================================================ */
  (function () {
    var wrap = document.querySelector('.pdp__main-img');
    var slides = wrap ? wrap.querySelectorAll('.pdp__slide') : [];
    var thumbsWrap = document.querySelector('.pdp__thumbs');
    var thumbs = document.querySelectorAll('.pdp__thumbs button');
    if (!slides.length) return;
    var current = 0;

    function setSlide(i) {
      var n = slides.length;
      current = ((i % n) + n) % n; // wrap around
      slides.forEach(function (s, idx) {
        var on = idx === current;
        s.classList.toggle('is-active', on);
        // A video slide fetches nothing until it is actually shown, then plays
        // muted; leaving it pauses it so a swipe past never keeps decoding.
        if (s.tagName === 'VIDEO') {
          if (on) {
            var src = s.getAttribute('data-src');
            if (src && !s.getAttribute('src')) { s.setAttribute('src', src); s.load(); }
            var p = s.play(); if (p && p.catch) p.catch(function () {});
          } else if (!s.paused) {
            s.pause();
          }
        }
      });
      thumbs.forEach(function (t) { t.classList.toggle('is-active', (parseInt(t.getAttribute('data-index'), 10) || 0) === current); });
    }
    window.LovelleShowSlide = setSlide; // lets the colour picker drive the main image

    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        if (thumb.hasAttribute('data-thumb-expand') && thumbsWrap) thumbsWrap.classList.remove('is-collapsed');
        setSlide(parseInt(thumb.getAttribute('data-index'), 10) || 0);
      });
    });

    // Prev / next arrow buttons
    var prev = wrap.querySelector('.pdp__nav--prev');
    var next = wrap.querySelector('.pdp__nav--next');
    if (prev) prev.addEventListener('click', function () { setSlide(current - 1); });
    if (next) next.addEventListener('click', function () { setSlide(current + 1); });

    // Touch swipe (mobile)
    var x0 = null, y0 = null;
    wrap.addEventListener('touchstart', function (e) {
      x0 = e.touches[0].clientX; y0 = e.touches[0].clientY;
    }, { passive: true });
    wrap.addEventListener('touchend', function (e) {
      if (x0 === null) return;
      var dx = e.changedTouches[0].clientX - x0;
      var dy = e.changedTouches[0].clientY - y0;
      // horizontal swipe only (ignore vertical scroll)
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
        setSlide(current + (dx < 0 ? 1 : -1));
      }
      x0 = null; y0 = null;
    }, { passive: true });
  })();

  /* ============================================================
     9. PDP VARIANT -> price / id / availability / sticky price
     ============================================================ */
  var pdpJson = document.querySelector('[data-product-json]');
  if (pdpJson) {
    var product = null;
    try { product = JSON.parse(pdpJson.textContent); } catch (e) {}
    var pform = document.querySelector('form[data-product-form]');
    if (product && pform) {
      var idField = pform.querySelector('[name="id"]');
      var priceEl = document.querySelector('.pdp__price [data-price]');
      var compareEl = document.querySelector('.pdp__price [data-compare-price]');
      var discountEl = document.querySelector('.pdp__price [data-discount]');
      var stickyPrice = document.querySelector('.sticky-atc__price');
      var submitSpan = pform.querySelector('[type="submit"] span');
      var submitBtn = pform.querySelector('[type="submit"]');
      var update = function () {
        var chosen = [];
        pform.querySelectorAll('.variant-group').forEach(function (g) {
          var sel = g.querySelector('input:checked');
          if (sel) chosen.push(sel.value);
        });
        var match = product.variants.find(function (v) {
          return v.options.every(function (o, i) { return o === chosen[i]; });
        }) || product.variants[0];
        if (!match) return;
        if (idField) idField.value = match.id;
        if (priceEl) priceEl.textContent = money(match.price);
        if (stickyPrice) stickyPrice.textContent = money(match.price);
        // Compare-at price + discount badge per variant
        var onSale = match.compare_at_price && match.compare_at_price > match.price;
        if (compareEl) {
          if (onSale) { compareEl.textContent = money(match.compare_at_price); compareEl.hidden = false; }
          else compareEl.hidden = true;
        }
        if (discountEl) {
          if (onSale) { discountEl.textContent = '−' + Math.round((match.compare_at_price - match.price) / match.compare_at_price * 100) + '%'; discountEl.hidden = false; }
          else discountEl.hidden = true;
        }
        if (submitSpan) submitSpan.textContent = match.available ? (H.strings.addToCart || '') : (H.strings.soldOut || '');
        if (submitBtn) submitBtn.disabled = !match.available;
      };
      pform.querySelectorAll('.variant-options input').forEach(function (i) {
        i.addEventListener('change', update);
      });
    }
  }

  /* ============================================================
     11. BEFORE / AFTER COMPARE SLIDER (drag: mouse + touch + keyboard)
     ============================================================ */
  document.querySelectorAll('[data-compare]').forEach(function (c) {
    var knob = c.querySelector('[data-compare-knob]');
    var dragging = false;
    function setPos(clientX) {
      var r = c.getBoundingClientRect();
      var pct = Math.max(0, Math.min(100, ((clientX - r.left) / r.width) * 100));
      c.style.setProperty('--pos', pct + '%');
      if (knob) knob.setAttribute('aria-valuenow', Math.round(pct));
    }
    if (window.PointerEvent) {
      c.addEventListener('pointerdown', function (e) { dragging = true; setPos(e.clientX); });
      window.addEventListener('pointermove', function (e) { if (dragging) setPos(e.clientX); });
      window.addEventListener('pointerup', function () { dragging = false; });
    } else {
      c.addEventListener('mousedown', function (e) { dragging = true; setPos(e.clientX); });
      window.addEventListener('mousemove', function (e) { if (dragging) setPos(e.clientX); });
      window.addEventListener('mouseup', function () { dragging = false; });
      c.addEventListener('touchstart', function (e) { setPos(e.touches[0].clientX); }, { passive: true });
      c.addEventListener('touchmove', function (e) { setPos(e.touches[0].clientX); }, { passive: true });
    }
    if (knob) {
      knob.addEventListener('keydown', function (e) {
        var cur = parseFloat(getComputedStyle(c).getPropertyValue('--pos')) || 50;
        if (e.key === 'ArrowLeft') { setVar(Math.max(0, cur - 4)); e.preventDefault(); }
        if (e.key === 'ArrowRight') { setVar(Math.min(100, cur + 4)); e.preventDefault(); }
        function setVar(v) { c.style.setProperty('--pos', v + '%'); knob.setAttribute('aria-valuenow', Math.round(v)); }
      });
    }
  });

  /* ============================================================
     12. STATS COUNT-UP (animate on scroll into view)
     ============================================================ */
  (function () {
    var nums = document.querySelectorAll('[data-countup]');
    if (!nums.length) return;
    function fmt(v, decimals) {
      try { return new Intl.NumberFormat('sv-SE', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(v); }
      catch (e) { return v.toFixed(decimals); }
    }
    function run(el) {
      var raw = el.getAttribute('data-countup') || '';
      var pre = el.getAttribute('data-prefix') || '';
      var suf = el.getAttribute('data-suffix') || '';
      var target = parseFloat(String(raw).replace(',', '.'));
      if (isNaN(target)) { el.textContent = pre + raw + suf; return; }
      var decimals = (String(raw).split('.')[1] || '').length;
      if (reduced) { el.textContent = pre + fmt(target, decimals) + suf; return; }
      var dur = 1300, t0 = null;
      requestAnimationFrame(function step(ts) {
        if (!t0) t0 = ts;
        var p = Math.min(1, (ts - t0) / dur);
        var eased = 1 - Math.pow(1 - p, 3);
        el.textContent = pre + fmt(target * eased, decimals) + suf;
        if (p < 1) requestAnimationFrame(step);
      });
    }
    if (!('IntersectionObserver' in window)) { nums.forEach(run); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) { if (en.isIntersecting) { run(en.target); io.unobserve(en.target); } });
    }, { threshold: 0.4 });
    nums.forEach(function (n) { io.observe(n); });
  })();

  /* ============================================================
     13. PRODUCT REVIEWS — pagination + photo lightbox
     ============================================================ */
  (function () {
    var list = document.querySelector('[data-reviews]');
    var pager = document.querySelector('[data-review-pager]');
    if (list && pager) {
      var cards = Array.prototype.slice.call(list.querySelectorAll('.rv'));
      var perPage = parseInt(list.getAttribute('data-per-page'), 10) || 6;
      var pageCount = Math.ceil(cards.length / perPage);
      var current = 1;
      function pageList(cur) {
        var keep = {}; [1, 2, pageCount - 1, pageCount, cur - 1, cur, cur + 1].forEach(function (n) { if (n >= 1 && n <= pageCount) keep[n] = 1; });
        var nums = Object.keys(keep).map(Number).sort(function (a, b) { return a - b; });
        var out = [], prev = 0;
        nums.forEach(function (n) { if (prev && n - prev > 1) out.push('…'); out.push(n); prev = n; });
        return out;
      }
      function renderPager() {
        var html = '<button type="button" data-nav="prev" aria-label="' + (H.strings.previous || '') + '"' + (current === 1 ? ' disabled' : '') + '>‹</button>';
        pageList(current).forEach(function (n) {
          if (n === '…') html += '<span class="rvp-dots">…</span>';
          else html += '<button type="button" data-page="' + n + '"' + (n === current ? ' class="is-active"' : '') + '>' + n + '</button>';
        });
        html += '<button type="button" data-nav="next" aria-label="' + (H.strings.next || '') + '"' + (current === pageCount ? ' disabled' : '') + '>›</button>';
        pager.innerHTML = html;
      }
      function showPage(page) {
        current = Math.max(1, Math.min(pageCount, page));
        cards.forEach(function (c, i) {
          c.style.display = (i >= (current - 1) * perPage && i < current * perPage) ? '' : 'none';
        });
        renderPager();
      }
      pager.addEventListener('click', function (e) {
        var b = e.target.closest('button');
        if (!b || b.disabled) return;
        var nav = b.getAttribute('data-nav');
        if (nav === 'prev') showPage(current - 1);
        else if (nav === 'next') showPage(current + 1);
        else if (b.getAttribute('data-page')) showPage(parseInt(b.getAttribute('data-page'), 10));
        var anchor = document.getElementById('omdomen');
        if (anchor) anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
      showPage(1);
    }

    // Photo lightbox (delegated; works across pages)
    var lb = document.querySelector('[data-rv-lightbox]');
    if (lb) {
      var lbImg = lb.querySelector('[data-rv-img]');
      function closeLb() { lb.classList.remove('is-open'); lb.setAttribute('aria-hidden', 'true'); updateScrollLock(); }
      document.addEventListener('click', function (e) {
        var ph = e.target.closest('.rv__photo');
        if (ph) {
          var full = ph.getAttribute('data-full');
          if (full && lbImg) { lbImg.src = full; lb.classList.add('is-open'); lb.setAttribute('aria-hidden', 'false'); document.body.style.overflow = 'hidden'; }
          return;
        }
        if (e.target.closest('[data-rv-close]')) closeLb();
      });
      document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && lb.classList.contains('is-open')) closeLb(); });
    }
  })();

  /* ============================================================
     14. COLLECTION FILTER & SORT DRAWER
     ============================================================ */
  (function () {
    var drawer = document.querySelector('.filter-drawer');
    if (!drawer) return;
    function openD() { drawer.classList.add('is-open'); drawer.setAttribute('aria-hidden', 'false'); updateScrollLock(); }
    function closeD() { drawer.classList.remove('is-open'); drawer.setAttribute('aria-hidden', 'true'); updateScrollLock(); }
    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-filter-open]')) { e.preventDefault(); openD(); return; }
      if (e.target.closest('[data-filter-close]')) { closeD(); return; }
      var head = e.target.closest('.filter-group__head');
      if (head) {
        var exp = head.getAttribute('aria-expanded') === 'true';
        head.setAttribute('aria-expanded', String(!exp));
        var body = head.nextElementSibling;
        if (body) body.hidden = exp;
      }
    });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') closeD(); });

    var form = drawer.querySelector('[data-filter-form]');
    var instock = drawer.querySelector('[data-instock-toggle]');
    var applyCount = drawer.querySelector('[data-apply-count]');
    function updateApplyCount() {
      if (!applyCount) return;
      var n = form ? form.querySelectorAll('input[type=checkbox]:checked, input[type=radio]:checked').length : 0;
      if (instock && instock.checked) n += 1;
      applyCount.textContent = n;
    }
    if (instock) {
      instock.addEventListener('change', function () {
        document.body.classList.toggle('hide-soldout', instock.checked);
        updateApplyCount();
      });
    }
    if (form) form.addEventListener('change', updateApplyCount);
    updateApplyCount();
  })();

  /* ============================================================
     15. BUNDLE — buy more, save more (per-pair colour + size)
     ============================================================ */
  (function () {
    var bundle = document.querySelector('[data-bundle]');
    if (!bundle) return;
    var product = null;
    var pj = document.querySelector('[data-product-json]');
    try { product = JSON.parse(pj.textContent); } catch (e) {}
    if (!product || !product.variants) return;
    var colorPos = parseInt(bundle.getAttribute('data-color-pos'), 10) || 0;
    var sizePos = parseInt(bundle.getAttribute('data-size-pos'), 10) || 0;
    var totalEl = bundle.querySelector('[data-bundle-total]');
    var saveEl = bundle.querySelector('[data-bundle-save]');
    var addBtn = bundle.querySelector('[data-bundle-add]');
    var qty = 1;

    /* ---- Colour -> image maps + build the dropdowns (shared helpers) ---- */
    var maps = colourImageMaps(product, colorPos);
    var colorImg = maps.img, colorImgPos = maps.pos;
    Array.prototype.forEach.call(bundle.querySelectorAll('.bundle__select'), function (sel) { buildCustomSelect(sel, colorImg); });

    /* ---- Tiers: reveal the chosen tier's pair pickers right below it ---- */
    function selectTier(input) {
      qty = parseInt(input.getAttribute('data-qty'), 10) || 1;
      Array.prototype.forEach.call(bundle.querySelectorAll('.bundle__group'), function (g) {
        var sel = g.contains(input);
        g.classList.toggle('is-selected', sel);
        var pk = g.querySelector('[data-picks]');
        if (pk) pk.hidden = !sel;
      });
      if (totalEl) totalEl.textContent = input.getAttribute('data-total-money') || '';
      var save = input.getAttribute('data-save-money');
      if (saveEl) {
        if (save && qty > 1) { saveEl.textContent = 'Du sparar ' + save + ' i kassan'; saveEl.hidden = false; }
        else saveEl.hidden = true;
      }
    }
    bundle.addEventListener('change', function (e) {
      if (!e.target.matches) return;
      if (e.target.matches('input[type=radio][data-qty]')) { selectTier(e.target); return; }
      if (e.target.matches('select[data-bundle-color]')) {
        var pos = colorImgPos[e.target.value];
        if (pos && window.LovelleShowSlide) {
          window.LovelleShowSlide(pos - 1);
          var g = document.querySelector('.pdp__gallery') || document.querySelector('.pdp__main-img');
          if (g) g.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    });
    var checked = bundle.querySelector('input[data-qty]:checked') || bundle.querySelector('input[data-qty]');
    if (checked) { checked.checked = true; selectTier(checked); }

    function findVariant(color, size) {
      return product.variants.find(function (vr) {
        var ok = true;
        if (colorPos) ok = ok && vr.options[colorPos - 1] === color;
        if (sizePos) ok = ok && vr.options[sizePos - 1] === size;
        return ok;
      });
    }
    if (addBtn) addBtn.addEventListener('click', function () {
      var ci = bundle.querySelector('input[data-qty]:checked');
      var group = ci ? ci.closest('.bundle__group') : null;
      var items = [], missing = false;
      var rows = group ? Array.prototype.slice.call(group.querySelectorAll('.bundle__pick')) : [];
      if (!rows.length) {
        items.push({ id: product.variants[0].id, quantity: qty });
      } else {
        rows.forEach(function (row) {
          var c = row.querySelector('[data-bundle-color]');
          var s = row.querySelector('[data-bundle-size]');
          var variant = findVariant(c ? c.value : null, s ? s.value : null);
          if (variant && variant.available !== false) items.push({ id: variant.id, quantity: 1 });
          else missing = true;
        });
      }
      if (missing || !items.length) { alert(H.strings.comboUnavailable || ''); return; }
      var merged = {};
      items.forEach(function (it) { merged[it.id] = (merged[it.id] || 0) + it.quantity; });
      var finalItems = Object.keys(merged).map(function (id) { return { id: parseInt(id, 10), quantity: merged[id] }; });
      addToCart(finalItems, addBtn, { showUpsell: true });
    });
  })();

  /* ============================================================
     16. STANDARD PRODUCT PAGE — colour/size dropdowns + image swap
         (same custom selects as the leggings bundle, single ATC)
     ============================================================ */
  (function () {
    var form = document.querySelector('[data-variant-form]');
    if (!form) return;
    var product = null;
    var pj = document.querySelector('[data-product-json]');
    try { product = JSON.parse(pj.textContent); } catch (e) {}
    if (!product || !product.variants) return;

    var selects = form.querySelectorAll('select[data-variant-option]');
    if (!selects.length) return;
    var colorSel = form.querySelector('select[data-variant-color]');
    var colorPos = colorSel ? (parseInt(colorSel.getAttribute('data-position'), 10) || 0) : 0;
    var maps = colourImageMaps(product, colorPos);
    Array.prototype.forEach.call(selects, function (sel) { buildCustomSelect(sel, maps.img); });

    var idField = form.querySelector('[name="id"]');
    var priceEl = document.querySelector('.pdp__price [data-price]');
    var compareEl = document.querySelector('.pdp__price [data-compare-price]');
    var discountEl = document.querySelector('.pdp__price [data-discount]');
    var submitBtn = form.querySelector('[type="submit"]');
    var submitSpan = submitBtn ? submitBtn.querySelector('span') : null;
    var stickyPrice = document.querySelector('.sticky-atc__price');

    function chosen() {
      var arr = [];
      Array.prototype.forEach.call(selects, function (s) {
        arr[(parseInt(s.getAttribute('data-position'), 10) || 1) - 1] = s.value;
      });
      return arr;
    }
    function update() {
      var ch = chosen();
      var match = product.variants.find(function (vr) {
        return vr.options.every(function (o, i) { return ch[i] == null || o === ch[i]; });
      }) || product.variants[0];
      if (!match) return;
      if (idField) idField.value = match.id;
      if (priceEl) priceEl.textContent = money(match.price);
      if (stickyPrice) stickyPrice.textContent = money(match.price);
      var onSale = match.compare_at_price && match.compare_at_price > match.price;
      if (compareEl) {
        if (onSale) { compareEl.textContent = money(match.compare_at_price); compareEl.hidden = false; }
        else compareEl.hidden = true;
      }
      if (discountEl) {
        if (onSale) { discountEl.textContent = '−' + Math.round((match.compare_at_price - match.price) / match.compare_at_price * 100) + '%'; discountEl.hidden = false; }
        else discountEl.hidden = true;
      }
      if (submitSpan) submitSpan.textContent = match.available ? (H.strings.addToCart || '') : (H.strings.soldOut || '');
      if (submitBtn) submitBtn.disabled = !match.available;
    }
    form.addEventListener('change', function (e) {
      if (!e.target.matches || !e.target.matches('select[data-variant-option]')) return;
      update();
      if (e.target.matches('[data-variant-color]')) {
        var pos = maps.pos[e.target.value];
        if (pos && window.LovelleShowSlide) window.LovelleShowSlide(pos - 1);
      }
    });
    update();
  })();

  /* ---- Initial cart bubble sync ---- */
  fetchCart();
})();
